/**
 * fetchPolygonElevationsCascading — EAS-4 wrapper around the 4-stage
 * elevation cascade (`services/dataProviders/elevation/cascadingSampler`).
 *
 * Pack: `docs/prompt-packs/Elevation_Averaging_Sampling_Prompt.md`.
 *
 * Why a wrapper
 * -------------
 * The cascade returns rich `ElevationSamplingResult` (per-stage audit
 * trail + source qualifier + emptyReason). The boundary-draw flow,
 * the KMZ-import path, and `refreshSiteElevation` all consume the
 * legacy `ElevationStats` envelope (`{min, max, avg, stdev,
 * pointsSampled}`). This wrapper:
 *
 *   1. invokes the cascade,
 *   2. computes min / max / avg / population stdev / pointsSampled
 *      from the raw samples (exact formula matches
 *      `fetchPolygonElevations`),
 *   3. returns a stats envelope augmented with the cascade's
 *      `source`, `stages`, and `emptyReason` so callers can stamp
 *      PRE chip metadata without re-running the cascade.
 *
 * The legacy `fetchPolygonElevations` is kept for back-compat — its
 * existing callers (SiteBoundaryDrawer, KMZ-import) keep their
 * exact behaviour for now. New code (`refreshSiteElevation`, the
 * siteEvidence producer's bootstrap path) opts into the cascade by
 * importing this entry-point instead.
 *
 * Numerical fidelity (CLAUDE.md):
 *   - Population stdev: σ = sqrt(Σ(xᵢ - μ)² / N). Same formula as
 *     fetchPolygonElevations (divisor = N, NOT N-1) — preserves the
 *     stamp on every existing SED.
 *   - min/max/avg are rounded to 1 decimal place to match the
 *     legacy helper's display contract.
 */

import {
  sampleElevationCascading,
  type ElevationSamplingResult,
  type ElevationSamplingStageRecord,
} from '../services/dataProviders/elevation/cascadingSampler';
import type { LatLng } from './geometry/sampleGrid';

export interface CascadingElevationStats {
  sourceQualification?: import('../types/elevationSourceQualification').ElevationSourceQualification;
  /** Minimum sampled elevation, metres ASL, rounded to 1dp. */
  min: number;
  /** Maximum sampled elevation, metres ASL, rounded to 1dp. */
  max: number;
  /** Mean of the sampled elevations, metres ASL, rounded to 1dp. */
  avg: number;
  /** Population stdev across the samples, metres ASL, rounded to 1dp.
   *  `null` when fewer than 2 samples (USGS centroid stage). */
  stdev: number | null;
  /** Number of sample points contributing to the stats. */
  pointsSampled: number;
  /** Stage-qualified provenance string (`google-elevation:vertex`,
   *  `usgs-epqs:centroid`, etc.). */
  source: ElevationSamplingResult['source'];
  /** Per-stage audit trail — surfaced in the PRE chip tooltip. */
  stages: ElevationSamplingStageRecord[];
}

export interface CascadingElevationEmpty {
  source: 'google-elevation:none';
  emptyReason: string;
  stages: ElevationSamplingStageRecord[];
}

export type CascadingElevationResult =
  | { kind: 'success'; stats: CascadingElevationStats }
  | { kind: 'empty'; meta: CascadingElevationEmpty };

function round1(n: number): number {
  return parseFloat(n.toFixed(1));
}

/**
 * Run the 4-stage cascade and shape the result for legacy callers.
 *
 * @param ring  Polygon vertices, `{lat,lng}[]`. Fewer than 3 vertices
 *              ⇒ `{kind: 'empty'}` with no upstream calls.
 */
export async function fetchPolygonElevationsCascading(
  ring: readonly LatLng[],
): Promise<CascadingElevationResult> {
  const cascade = await sampleElevationCascading(ring);

  if (cascade.attemptResult === 'empty' || cascade.samples.length === 0) {
    return {
      kind: 'empty',
      meta: {
        source: 'google-elevation:none',
        // Cascade always populates emptyReason on the empty branch,
        // but be defensive — a future refactor might omit it.
        emptyReason:
          cascade.emptyReason ??
          'Provider returned no data after every sampling strategy.',
        stages: cascade.stages,
      },
    };
  }

  const elevs = cascade.samples.map(s => s.elevationMeters);
  const avg = elevs.reduce((a, b) => a + b, 0) / elevs.length;
  // Population standard deviation (divisor = N) — matches the
  // existing fetchPolygonElevations contract; tested in
  // fetchPolygonElevations.test.ts ("Population stdev of
  // {10,15,20,25,30} = sqrt(50) ≈ 7.07 → 7.1").
  const stdev =
    elevs.length > 1
      ? Math.sqrt(elevs.reduce((s, e) => s + (e - avg) ** 2, 0) / elevs.length)
      : null;

  return {
    kind: 'success',
    stats: {
      min: round1(Math.min(...elevs)),
      max: round1(Math.max(...elevs)),
      avg: round1(avg),
      stdev: stdev == null ? null : round1(stdev),
      pointsSampled: elevs.length,
      source: cascade.source,
      stages: cascade.stages,
      ...(cascade.sourceQualification ? { sourceQualification: cascade.sourceQualification } : {}),
    },
  };
}
